import { Color, colorToGraphicsOpts } from '@cadstrom/renderer';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { ChromePicker } from 'react-color';

import { useActiveDesignContext } from '../../context/active-design-context.js';
import { useDrawingManagerContext } from '../../context/drawing-manager-context.js';
import { deserializeGraphicRenderState, serializeGraphicRenderState } from '../../sync/bridges/layer-bridge.js';
import { useCadRendererLayerSync } from '../../sync/cad-renderer-sync-context.js';

import type { GraphicRenderStateOpts } from '@cadstrom/renderer';
import type { Layer } from '@cadstrom/sigma-engine-shared';
import type { ColorResult } from 'react-color';

export type LayersColorPickerOpts = {
	layer: Layer;
	anchorEl: HTMLDivElement;
};

export type LayersColorPickerProps = LayersColorPickerOpts & {
	onPickComplete: () => void;
	open: boolean;
};

export function LayersColorPicker(props: LayersColorPickerProps) {
	const theme = useTheme();
	const drawingManagerContext = useDrawingManagerContext();
	const { activeDesign } = useActiveDesignContext();
	const { layerState, setLayerColorOverride } = useCadRendererLayerSync(activeDesign.layerSet);
	const syncedColor = layerState?.colorOverrides[props.layer.id];
	const [currentColor, setCurrentColor] = React.useState<GraphicRenderStateOpts>(
		syncedColor ?
			deserializeGraphicRenderState(syncedColor)
		:	drawingManagerContext.drawingManager!.colorProvider.getColorForLayer(props.layer.id),
	);

	React.useEffect(() => {
		if (syncedColor) {
			setCurrentColor(deserializeGraphicRenderState(syncedColor));
		}
	}, [syncedColor]);

	const currentColorObj = React.useMemo(() => {
		const parsed = new Color.default(currentColor.selectionNeutral.tint).alpha(currentColor.selectionNeutral.alpha);
		return {
			r: parsed.red(),
			g: parsed.green(),
			b: parsed.blue(),
			a: parsed.alpha(),
		};
	}, [currentColor]);

	function handleChange(color: ColorResult) {
		const parsed = Color.default.rgb(color.rgb.r, color.rgb.g, color.rgb.b);
		const newOpts = colorToGraphicsOpts(parsed.rgbNumber(), color.rgb.a);
		setCurrentColor(newOpts);
		setLayerColorOverride(props.layer.id, serializeGraphicRenderState(newOpts));
	}

	return (
		<Popover open={props.open} anchorEl={props.anchorEl} onClose={() => props.onPickComplete()}>
			<ChromePicker
				color={currentColorObj}
				onChange={handleChange}
				styles={{
					default: {
						body: {
							backgroundColor: theme.vars.palette.background.paper,
							color: theme.vars.palette.text.primary,
						},
					},
				}}
			/>
		</Popover>
	);
}
